import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import { EmailTemplateLogo } from "ui/EmailTemplateLogo"
import PhotoSlider from "components/common/PhotoSlider"
import DOMPurify from "dompurify"
import styles from "./Template.module.css"

const stripHtml = (html = "") => DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })

// Truncate HTML to a visible-character limit while keeping tags balanced, so
// formatting (bold/italic/etc.) is preserved and never cut mid-tag.
const truncateHtml = (html, limit) => {
  const sanitized = DOMPurify.sanitize(html ?? "")
  if (stripHtml(sanitized).length <= limit) return sanitized

  const source = new DOMParser().parseFromString(sanitized, "text/html").body
  const result = window.document.createElement("div")
  let remaining = limit
  let lastTextNode = null

  const walk = (node, into) => {
    for (const child of node.childNodes) {
      if (remaining <= 0) break
      if (child.nodeType === window.Node.TEXT_NODE) {
        const text = child.textContent.slice(0, remaining)
        remaining -= text.length
        lastTextNode = window.document.createTextNode(text)
        into.appendChild(lastTextNode)
      } else if (child.nodeType === window.Node.ELEMENT_NODE) {
        const clone = child.cloneNode(false)
        into.appendChild(clone)
        walk(child, clone)
      }
    }
  }

  walk(source, result)
  // Append the ellipsis to the last text node so it stays inline with the
  // truncated text instead of dropping onto a new line after a block element.
  if (lastTextNode) lastTextNode.textContent += "..."
  return result.innerHTML
}

const Template = ({ template, className, style, ...rest }) => {
  const { name, heading, button_text, button_url, category, paragraph_text, signature, images, cropped_photos, template_type, custom_logo_url } =
    template

  const isCentered = ["automated", "small_gallery", "large_gallery"].includes(category)
  const alignClass = isCentered ? styles.centered : styles.leftAligned
  const withPhoto = category === "large_gallery" || category === "small_gallery" || category === "automated"
  const isEmail = template_type === "email"
  const isSecondBasic = category === "second_basic"
  const isAutomated = category === "automated"

  const paragraphHtml = truncateHtml(paragraph_text, withPhoto ? 160 : 520)

  const containerClasses = [styles.container]
  if (className) containerClasses.push(className)

  return (
    <div className={containerClasses.join(" ")} style={style} {...rest}>
      <div className={`${isEmail ? styles.body : styles.bodySms} ${alignClass}`}>
        {isEmail && <EmailTemplateLogo logoUrl={custom_logo_url} height={30} className={styles.logo} />}
        {heading && <h5 className={`${styles.heading} ${alignClass}`}>{heading}</h5>}
        {!isAutomated && <p className={`${styles.greeting} ${alignClass}`}>{"Hello [Client Name]"}</p>}
        <div className={`${styles.paragraphBlock} ${alignClass}`}>
          <p className={styles.paragraph} dangerouslySetInnerHTML={{ __html: paragraphHtml }} />
          {isSecondBasic && isEmail && (
            <Button type="primary" className={styles.buttonRow} size="small">
              {button_text}
            </Button>
          )}
        </div>
        {withPhoto && isEmail && (
          <div className={styles.photoWrapper}>
            <PhotoSlider photos={images} cropped_photos={cropped_photos} preview className={styles.photoSlider} />
          </div>
        )}
        {isAutomated ? (
          <h5 className={styles.automatedQuestion}>{"Did you enjoy your experience?"}</h5>
        ) : (
          !withPhoto && (
            <div className={styles.signatureBlock}>
              {!isEmail && (
                <>
                  <Button type="link" block>
                    {button_url}
                  </Button>
                  <br />
                </>
              )}
              <div>{signature}</div>
              <div className={styles.guideProfileName}>{"[Guide profile name]"}</div>
            </div>
          )
        )}
      </div>
      <div className={isAutomated ? styles.footerAutomated : styles.footerDefault}>
        <span className={styles.footerName}>{name}</span>
        {isAutomated && (
          <>
            <br />
            <small className={styles.footerAutomatedLabel}>(automated)</small>
          </>
        )}
      </div>
    </div>
  )
}

Template.propTypes = {
  template: PropTypes.shape({
    name: PropTypes.string.isRequired,
    heading: PropTypes.string,
    button_text: PropTypes.string,
    button_url: PropTypes.string,
    category: PropTypes.string.isRequired,
    paragraph_text: PropTypes.string.isRequired,
    signature: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(PropTypes.string),
    cropped_photos: PropTypes.arrayOf(PropTypes.string),
    template_type: PropTypes.string.isRequired,
    custom_logo_url: PropTypes.string
  }).isRequired,
  className: PropTypes.string,
  style: PropTypes.object
}

export default memo(Template)
